import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { JwtAuthService } from './auth/jwt-auth.service';

/** Mapeamento entre o produto do ERP e o anúncio no Mercado Livre. */
export interface ProdutoAnuncio {
  id: number;
  itemId?: string;
  variationId?: string;
  categoryId?: string;
  listingTypeId?: string;
  condicao?: string;
  permalink?: string;
  status?: string;
  precoPublicado?: number;
  qtdePublicada?: number;
  ultimaSincronizacao?: string;
  ultimoErro?: string;
  produto?: any;
}

/** Uma linha do formulário de atributos que a categoria exige. */
export interface AtributoAnuncio {
  code: string;
  nome: string;
  tipo?: string;
  obrigatorio: boolean;
  valor?: string;
  valoresPermitidos?: string[];
  /** `spec` = digitado aqui; `cadastro` = veio do produto e não se edita nesta tela. */
  origem?: string;
  produtoSpecId?: number;
}

/** Conferência de prontidão: o que impede de publicar e o que é só ressalva. */
export interface ValidacaoAnuncio {
  produtoId: number;
  descricao?: string;
  mapeado: boolean;
  itemId?: string;
  categoryId?: string;
  status?: string;
  pronto: boolean;
  impedimentos: string[];
  avisos: string[];
  atributosFaltando: string[];
}

/** O que a remessa separou para o canal. */
export interface SaldoCanal {
  produtoId: number;
  qtde: number;
  preco: number;
}

export interface ResultadoPublicacao {
  produtoId: number;
  descricao?: string;
  sucesso: boolean;
  itemId?: string;
  permalink?: string;
  preco?: number;
  qtde?: number;
  status?: string;
  mensagem?: string;
  avisos?: string[];
}

/** Sugestão do preditor de categoria do ML. */
/** Resultado de uma operação feita a partir de um lançamento. */
export interface LotePublicacao {
  resultados: ResultadoPublicacao[];
  /** Controle do lançamento de publicação, quando pedido e gerado. */
  controleDocumento?: number;
  /** Publicou, mas o documento falhou. */
  avisoDocumento?: string;
}

/** Sugestão do preditor de categoria do ML. */
export interface SugestaoCategoria {
  category_id: string;
  category_name: string;
  domain_name?: string;
}

/**
 * Mapeamento, atributos e publicação de um produto no Mercado Livre.
 *
 * Separado do `MercadoLivreService` (que trata só do pareamento da conta) porque esta é a parte
 * usada dentro do cadastro de produto, não na tela de parâmetros.
 */
@Injectable({
  providedIn: 'root'
})
export class MercadoLivreAnuncioService {

  readonly path = '/mercadolivre';
  readonly pathSpec = '/marketplace/produto-spec';

  constructor(private http: HttpClient, public jwtAuthService: JwtAuthService) {}

  private get api(): string {
    return this.jwtAuthService.apiUrl;
  }

  /** Mapeamento do produto. O backend responde 204 (corpo nulo) quando ainda não há. */
  getAnuncio(produtoId: number): Observable<ProdutoAnuncio> {
    return this.http.get<ProdutoAnuncio>(`${this.api + this.path}/anuncio/produto/${produtoId}`);
  }

  vincular(produtoId: number, itemId: string): Observable<ProdutoAnuncio> {
    return this.http.post<ProdutoAnuncio>(
      `${this.api + this.path}/anuncio/produto/${produtoId}/vincular?itemId=${encodeURIComponent(itemId)}`, null);
  }

  preparar(produtoId: number, categoryId: string, listingTypeId: string, condicao: string): Observable<ProdutoAnuncio> {
    return this.http.post<ProdutoAnuncio>(`${this.api + this.path}/anuncio/produto/${produtoId}/preparar`,
      { categoryId, listingTypeId, condicao });
  }

  sugerirCategorias(produtoId: number): Observable<SugestaoCategoria[]> {
    return this.http.get<SugestaoCategoria[]>(`${this.api + this.path}/anuncio/produto/${produtoId}/categorias`);
  }

  getAtributos(produtoId: number, somenteObrigatorios = true): Observable<AtributoAnuncio[]> {
    return this.http.get<AtributoAnuncio[]>(
      `${this.api + this.path}/anuncio/produto/${produtoId}/atributos?obrigatorios=${somenteObrigatorios}`);
  }

  validar(produtoId: number): Observable<ValidacaoAnuncio> {
    return this.http.get<ValidacaoAnuncio>(`${this.api + this.path}/anuncio/produto/${produtoId}/validar`);
  }

  /** Remove o mapeamento (pelo id dele, não do produto). O anúncio continua no Mercado Livre. */
  desvincular(anuncioId: number): Observable<any> {
    return this.http.delete<any>(`${this.api + this.path}/anuncio/${anuncioId}`);
  }

  /** Quanto está separado para o canal (pendência LS) e por qual preço. */
  getSaldo(produtoId: number): Observable<SaldoCanal> {
    return this.http.get<SaldoCanal>(`${this.api + this.path}/publicacao/saldo/produto/${produtoId}`);
  }

  publicar(produtoId: number, preco?: number): Observable<ResultadoPublicacao> {
    const query = preco ? `?preco=${preco}` : '';
    return this.http.post<ResultadoPublicacao>(
      `${this.api + this.path}/publicacao/produto/${produtoId}${query}`, null);
  }

  /**
   * Valores que o ML sugere para um atributo (marca, modelo).
   *
   * Sugestão, não restrição: o campo continua aceitando texto livre. Produto de nicho não está no
   * catálogo do ML, e um campo fechado impediria de anunciá-lo.
   */
  getValoresSugeridos(produtoId: number, code: string): Observable<string[]> {
    return this.http.get<string[]>(
      `${this.api + this.path}/anuncio/produto/${produtoId}/atributos/${encodeURIComponent(code)}/valores`);
  }

  /**
   * Publica no Mercado Livre os itens deste lançamento de remessa.
   *
   * A quantidade enviada é a remessa em aberto, não a do item: o item diz o que foi remetido
   * desta vez, o anúncio mostra o total que ainda está no canal.
   */
  publicarLancamento(controle: number): Observable<LotePublicacao> {
    return this.http.post<LotePublicacao>(
      `${this.api + this.path}/publicacao/lancamento/${controle}`, null);
  }

  /**
   * Atualiza no Mercado Livre o preço dos itens deste lançamento.
   *
   * Não mexe na quantidade e não cria anúncio para produto ainda não publicado.
   */
  atualizarPrecosLancamento(controle: number): Observable<LotePublicacao> {
    return this.http.post<LotePublicacao>(
      `${this.api + this.path}/publicacao/lancamento/${controle}/precos`, null);
  }

  /**
   * Grava os atributos pelo código, em lote.
   *
   * Valor em branco apaga o atributo. O retorno já é a lista atualizada do produto.
   */
  salvarAtributos(produtoId: number, valores: { code: string, descricao?: string, valor: string }[]): Observable<any> {
    return this.http.post<any>(`${this.api + this.pathSpec}/produto/${produtoId}`, valores);
  }
}
